import * as Sentry from "@sentry/react";
import { Component, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Matches the error thrown when a dynamic `import()` 404s — typically
// because a new deploy replaced the hashed chunk the running app is
// trying to fetch. Self-healing strategy: reload the tab to pick up the
// fresh index.html + new chunk names. Don't report to Sentry (it's a
// normal consequence of a deploy, not a real bug).
function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message || "";
  const name = error.name || "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    (name === "ChunkLoadError") ||
    // Vite's preload helper wraps the network error in a ModuleError
    msg.includes("Unable to preload CSS")
  );
}

// Guard against reload loops. If the page just reloaded because of a
// chunk error and the same error comes back, fall through to the normal
// error UI instead of looping forever.
const RELOAD_GUARD_KEY = "trace_chunk_reload_at";
const RELOAD_GUARD_WINDOW_MS = 60_000;

function shouldAttemptReload(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) return true;
    const last = Number(raw);
    if (Number.isNaN(last)) return true;
    return Date.now() - last > RELOAD_GUARD_WINDOW_MS;
  } catch {
    return true;
  }
}

function markReloadAttempt() {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // ignore (Safari private mode, etc.)
  }
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Chunk-load errors mean the app's HTML references a hashed bundle
    // that no longer exists after a deploy. Trigger a full reload
    // synchronously so the user picks up the fresh index.html without
    // seeing the error screen. Guarded against reload loops.
    if (isChunkLoadError(error) && shouldAttemptReload()) {
      markReloadAttempt();
      window.location.reload();
      // Keep hasError=false until reload completes — avoids a flash of
      // the fallback UI.
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isChunkLoadError(error)) {
      // Reload is in flight (or we're inside the guard window). Don't
      // pollute Sentry with what is structurally a deploy race.
      console.info("Chunk load error — self-healing reload triggered.");
      return;
    }
    console.error("ErrorBoundary caught:", error, info.componentStack);
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg-2 text-ink">
          <div className="max-w-md space-y-4 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-bad" />
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-ink-3">
              {this.state.error?.message || "An unexpected error occurred."}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="btn btn--accent px-4 py-2 text-sm"
              >
                Reload page
              </button>
              <a
                href="/dashboard"
                className="btn btn--ghost px-4 py-2 text-sm"
              >
                Go to Dashboard
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
